import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Row, Col, Card, CardBody, Container } from "reactstrap";
import moment from "moment";
import { Link } from "react-router-dom";
import { PopoverBody } from 'reactstrap';
import {useHistory, useParams} from "react-router-dom";

import PopoverFormula from '../../../../share-components/ui/PopoverFormula';
import BootstrapTable from "../../../../share-components/table/BootstrapTable";
import DateRangePickerPrimary from "../../../../share-components/ui/DateRangePickerPrimary";
import Breadcrumb from "../../../../layout/breadcrumb";
import ShopifyPayoutDetailsSummary from "./components/ShopifyPayoutDetailsSummary";

import withReducer from "../../../../store/withReducer";
import reportReducer from "../../index";

import {
    getShopifyPayoutDetail,
    exportShopifyPayoutDetail
} from "../store/payoutReportSlice";

import {
    formatterCurrency
} from "../../../../utils/common";


const ShopifyPayoutDetails = (props) => {
    const {id} = useParams();

    const financesSummaryState = useSelector(
        ({ report }) => report.financesSummary
    );

    const payoutReportState = useSelector(
        ({ report }) => report.payoutReport
    );

    const dispatch = useDispatch();
    const queryParams = new URLSearchParams(window.location.search);

    // helper to ensure a value is a valid date/moment/string parseable by moment
    const isValidDate = (d) => d !== undefined && moment(d).isValid();

    // parse query params and normalize invalid dates to undefined
    const _rawEnd = queryParams.has('end_date') ? new Date(queryParams.get('end_date')) : undefined;
    const _rawStart = queryParams.has('start_date') ? new Date(queryParams.get('start_date')) : undefined;
    const endDateObj = isValidDate(_rawEnd) ? _rawEnd : undefined;
    const startDateObj = isValidDate(_rawStart) ? _rawStart : undefined;

    // initialize dateRange with validated dates (undefined when absent/invalid)
    const [dateRange, setDateRange] = useState([startDateObj, endDateObj]);
    const [startDate, endDate] = dateRange;

    // safe disabledDate: return false for falsy/invalid input
    const afterToday = () => {
        return (date) => {
            if (!isValidDate(date)) return false;
            return date.getTime() > Date.now();
        };
    };

    const [loading, setLoading] = useState(true);
    const [exportBtnDisabled, setExportBtnDisabled] = useState(false);

    const [lastFillters, setLastFillters] = useState(null);
    const [filter, setFilter] = useState({
        page: 1,
        pageSize: 10,
        // set initial dates to null if not provided
        start_date: isValidDate(startDate) ? moment(startDate).format("YYYY-MM-DD") : null,
        end_date: isValidDate(endDate) ? moment(endDate).format("YYYY-MM-DD") : null,
    });

    const dataMenu = [
        {
            type: 0,
            text: "Reports",
        },
        {
            type: 1,
            text: "Shopify Payouts",
            link: '/report/payments/shopify-payouts'
        },
        {
            type: 0,
            text: "Payout Details",
        },
    ];

    const getReport = () => {
        if (JSON.stringify(filter) == JSON.stringify(lastFillters)) return;

        setLastFillters({ ...filter });
        setLoading(true);

        dispatch(
            getShopifyPayoutDetail({...filter, ...{id: id}})
        ).then((res) => {
            if (!res.error) {
                setLoading(false);
            }
        });
    };

    useEffect(() => {
        getReport();
    }, [dispatch, filter]);

    const tableColumns = [
        {
            name: 'Payout Date',
            selector: row => moment(row.transaction_date).format("MMM DD, YYYY"),
            left: true,
        },
        {
            name: 'Order Name',
            selector: row => row.order_name,
            left: true,
        },
        {
            name: 'Type',
            selector: row => row.type,
            center: true,
        },
        {
            name: 'Currency',
            selector: row => row.currency,
            center: true,
        },
        {
            name: 'Amount',
            selector: row => row.amount,
            right: true,
        },
        {
            name: 'Charge Fee',
            selector: row => row.charge_fee,
            right: true,
        },
        {
            name: 'Net Amount',
            selector: row => row.net_amount,
            right: true,
        },
    ];

    const onPageSizeChange = (newPageSize) => {
        setFilter(prev => ({
            ...prev,
            page: 1,
            pageSize: newPageSize === 'All' ? payoutReportState.shopifyPayoutDetailTableRow : newPageSize
        }));
    }

    const onPageChange = (e) => {
        setFilter({ ...filter, page: e })
    }

    useEffect(() => {
        setFilter(prev => ({
            ...prev,
            start_date: isValidDate(startDate) ? moment(startDate).format("YYYY-MM-DD") : null,
            end_date: isValidDate(endDate) ? moment(endDate).format("YYYY-MM-DD") : null,
        }));
    }, [startDate, endDate]);
    
    const exportFile = () => {
        if (exportBtnDisabled) return;
        setExportBtnDisabled(true);

        dispatch(
            exportShopifyPayoutDetail({...filter, ...{id: id}})
        ).then((res) => {
            const link = document.createElement("a");
            link.href = res.payload;
            link.setAttribute("download", "");
            document.body.appendChild(link);
            link.click();
            link.remove();
            setExportBtnDisabled(false);
        });
    };

    return (
        <Fragment>
            <Breadcrumb title="Shopify Payout Details" data={dataMenu} />
            <Container fluid={true} className="finances-summary-page feature-products">
                <Card className="px-3">
                    <Row>
                        <Col md="4">
                            <div className="p-3">
                                <DateRangePickerPrimary
                                    dateRange={dateRange}
                                    dateRangeChange={(date) => {
                                        setDateRange(date);
                                    }}
                                    // only provide defaultValue when we have validated dates
                                    defaultValue={(isValidDate(startDateObj) || isValidDate(endDateObj)) ? [startDateObj, endDateObj] : undefined}
                                    disabledDate={afterToday()}
                                />
                            </div>
                        </Col>
                        <Col md="8" className="d-flex align-items-center justify-content-end">
                            <div className="p-3">
                                <Button
                                    type="button"
                                    color="primary"
                                    onClick={() => {
                                        exportFile();
                                    }}
                                    disabled={exportBtnDisabled}
                                >
                                    <i className={exportBtnDisabled ? "fa fa-spinner fa-spin mr-1" : "fa fa-download mr-1"}></i>
                                    Export Report
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card>

                <ShopifyPayoutDetailsSummary payoutReportState={payoutReportState} />

                <Card className="px-3">
                    <CardBody>
                        <BootstrapTable
                            headerColumns={tableColumns}
                            gridData={payoutReportState.shopifyPayoutDetailLists}
                            noAction
                            progressPending={loading}
                            paginationServer
                            paginationTotalRows={payoutReportState.shopifyPayoutDetailTableRow}
                            currentPage={filter.page}
                            perPage={filter.pageSize}
                            onChangePage={onPageChange}
                            onChangeRowsPerPage={onPageSizeChange}
                            size="md"
                        />
                    </CardBody>
                </Card>
            </Container>
        </Fragment>
    );
}

export default withReducer([{ report: reportReducer }])(ShopifyPayoutDetails);